/**
 * HVILKE MODULER HAR HUSET? (sak 238, Atle 02.10; migrering 0483, 0922.)
 *
 * Seks moduler — Festival, Spillested, Spillesteder, Konserter, Booking, Byrå — valgt per
 * organisasjon i Control Room. `/v1/me` har dem på hvert medlemskap, og aktøren bærer dem til
 * `can()`, som sier nei med `module_disabled` når en ressurs hører til en modul huset ikke har
 * (G1, `modules.ts` i shared). Menyen og rutene spør `can()`; ingen egen modulsjekk her lenger.
 *
 * Unntaket er OVERGANGEN: et API som ennå ikke sender `modules` (eldre bygg, eldre cache). Da
 * utleder `deriveModules` det samme som basen (0922), så menyen ikke endrer seg i mellomtiden.
 */
import type { MeResponse, OrganizationKind, OrganizationModule } from '@tvitvi/shared';

/** Modulene `/v1/me` sier at den aktive organisasjonen har, eller `undefined` når feltet mangler. */
export function declaredModulesOf(
  me: MeResponse | null,
): readonly OrganizationModule[] | undefined {
  if (me === null) return undefined;
  return me.memberships.find((entry) => entry.organizationId === me.activeOrganizationId)?.modules;
}

/**
 * ER MODULEN PÅ I DEN AKTIVE ORGANISASJONEN? (246.6, regel 4: «Modulene er den eneste regelen».)
 * Uten `modules` i `/v1/me` er svaret nei — typen spørres ikke.
 */
export function hasModule(me: MeResponse | null, module: OrganizationModule): boolean {
  return declaredModulesOf(me)?.includes(module) === true;
}

/**
 * HVILKEN SIDE AV BOOKINGEN LESER HUSET FRA? Forespørselen og budet har hvert sitt hjem: byråets
 * forespørsel åpnes på `/artists`, festivalens bud på `/booking` (avgjørelse 185). Før 246.6 avgjorde
 * typen det; nå modulene. Et hus med Artister og uten Booking leser som byrå. Har huset begge, er det
 * Booking-siden, som før (en festival med Artister hadde typen festival).
 */
export function bookingSideOf(me: MeResponse | null): 'agency' | 'festival' {
  return hasModule(me, 'agency') && !hasModule(me, 'booking') ? 'agency' : 'festival';
}

export interface ModuleFacts {
  kind: OrganizationKind;
  /** En levende festival av typen `festival`. */
  hasFestival: boolean;
  /** Et levende spillested. */
  hasVenue: boolean;
  /** Konserter, eller bryteren fra 0456 (`/concerts/presence`). */
  hasConcerts: boolean;
}

/**
 * Regelen fra `app.derive_organization_modules` (0922): det menyen viste før modulene ble valgt,
 * med Spillesteder for festival og byrå, og Konserter for et byrå (253.58). Bare for overgangen.
 */
export function deriveModules(facts: ModuleFacts): ReadonlySet<OrganizationModule> {
  const { kind, hasFestival, hasVenue, hasConcerts } = facts;
  const on = new Set<OrganizationModule>();
  if (kind === 'festival' && (hasFestival || (!hasVenue && !hasConcerts))) on.add('festival');
  if (kind === 'festival' && hasVenue) on.add('venue');
  if (kind === 'festival' || kind === 'agency') on.add('venues');
  if (kind === 'agency' || (kind === 'festival' && (hasConcerts || (!hasFestival && !hasVenue)))) {
    on.add('concerts');
  }
  if (kind === 'festival') on.add('booking');
  if (kind === 'agency') on.add('agency');
  return on;
}
